/**
 * Normalise un numéro nord-américain vers (xxx) xxx-xxxx.
 *
 * Les sources (fixtures, legacy Supabase) arrivent dans des formats
 * hétérogènes (tirets, tiret cadratin, espaces). Un numéro qui n'a pas
 * exactement 10 chiffres est rendu tel quel plutôt que tronqué.
 */
export function formatPhone(raw: string | null | undefined): string {
  if (!raw) return raw ?? ''
  const digits = raw.replace(/\D/g, '')
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
  if (local.length !== 10) return raw
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`
}

/**
 * Formate au fil de la saisie, sans attendre les 10 chiffres.
 * Utilisé sur les champs de formulaire (l'utilisateur voit le format se
 * construire), contrairement à formatPhone qui n'agit qu'à l'affichage.
 */
export function formatPhoneAsYouType(raw: string): string {
  const all = raw.replace(/\D/g, '')
  // Un numéro collé avec l'indicatif « +1 » garde ses 10 chiffres locaux.
  const digits = (all.length > 10 && all.startsWith('1') ? all.slice(1) : all).slice(0, 10)
  if (digits.length === 0) return ''
  if (digits.length < 4) return `(${digits}`
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

/**
 * Clé de comparaison d'un téléphone nord-américain : chiffres seulement, préfixe « 1 » retiré
 * sur 11 chiffres; `undefined` sous 10 chiffres (jamais de correspondance sur un numéro
 * partiel). Miroir de `public.phone_digits_v1` côté base — les deux doivent rester identiques.
 */
export function phoneDigits(raw: string | null | undefined): string | undefined {
  const digits = (raw ?? '').replace(/\D/g, '')
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
  return local.length >= 10 ? local : undefined
}

/** Message unique des champs de téléphone invalides (règle AGENTS.md). */
export const PHONE_INVALID_MESSAGE = 'Le numéro doit contenir 10 chiffres : (xxx) xxx-xxxx.'

/**
 * Règle des champs de téléphone : exactement 10 chiffres, préfixe « 1 » retiré sur 11 chiffres.
 * Le champ vide n'est pas jugé ici : l'obligation reste au formulaire.
 */
export function isValidPhone(raw: string | null | undefined): boolean {
  const digits = (raw ?? '').replace(/\D/g, '')
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
  return local.length === 10
}

/** Erreur à afficher sous un champ de téléphone : `undefined` si vide ou valide. */
export function phoneFieldError(raw: string | null | undefined): string | undefined {
  return raw?.trim() && !isValidPhone(raw) ? PHONE_INVALID_MESSAGE : undefined
}
